/**
 * Minew Technologies adapter — IOT-VENDOR-MINEW-1.
 *
 * BLE asset tracking and environmental sensor integration via Minew Cloud API webhook.
 * Supports: S1+ (asset tag), E6 (temperature/humidity sensor), MSB (smart beacon).
 *
 * Integration level: STUB — telemetry ingestion only.
 * Config push requires Minew Cloud API credentials (deferred to IOT-VENDOR-MINEW-2).
 * Position is cloud-computed from gateway RSSI triangulation (not GPS).
 *
 * HAL-WRITE-PATH-DOCTRINE-1: adapter does NOT write telemetry directly.
 */

import { NotImplementedAdapter } from '../NotImplementedAdapter.js';
import { VendorNotIntegratedError } from '../VendorNotIntegratedError.js';

const MINEW_CAPABILITIES = {
  configKeys: {
    reporting_interval_sec: {
      pushable: true,
      readable: true,
      range: [10, 3600],
      requiresActivation: false,
      unit: 'seconds',
      description: 'Beacon advertisement interval',
    },
    temp_alert_max_c: {
      pushable: true,
      readable: true,
      range: [-40, 85],
      requiresActivation: false,
      unit: '°C',
      description: 'Temperature alert max (E6 only)',
    },
    temp_alert_min_c: {
      pushable: true,
      readable: true,
      range: [-40, 85],
      requiresActivation: false,
      unit: '°C',
      description: 'Temperature alert min (E6 only)',
    },
  },
  telemetryTypes: [
    'temperature_c',
    'humidity_pct',
    'battery_soc_pct',
  ],
  alertingMode: 'cloud',
  otaSupported: false,
  attestationSupported: false,
  rollbackSupported: false,
  pushLatencyExpectedMs: 5000,
};

export class MinewAdapter extends NotImplementedAdapter {
  /**
   * @param {import('../../contracts/DeviceContract.ts').DeviceRecord} device
   */
  constructor(device) {
    super(device, 'minew', MINEW_CAPABILITIES);

    /** @type {import('../../contracts/DeviceContract.ts').NormalizedReading[]} */
    this._readings = [];
    /** @type {Array<import('../../contracts/DeviceContract.ts').TelemetryHandler>} */
    this._telemetrySubscribers = [];
  }

  // ── Telemetry — STUB implementation ─────────────────────────────────────────

  /**
   * Feed a normalized reading into the adapter (called by iotIngest CF).
   * Notifies all live subscribers.
   *
   * @param {import('../../contracts/DeviceContract.ts').NormalizedReading} reading
   */
  ingestReading(reading) {
    this._readings.push(reading);
    for (const handler of this._telemetrySubscribers) {
      try { handler(reading); } catch { /* subscriber errors must not crash ingest */ }
    }
  }

  /**
   * Return readings for the requested time window and sensor types.
   *
   * @param {import('../../contracts/DeviceContract.ts').TimeWindow} window
   * @param {string[]} [sensorTypes]
   */
  async getTelemetry(window, sensorTypes) {
    let readings = this._readings;
    if (window) {
      const from = new Date(window.from).getTime();
      const to   = new Date(window.to).getTime();
      readings = readings.filter((r) => {
        const ts = new Date(r.timestamp).getTime();
        return ts >= from && ts <= to;
      });
    }
    if (sensorTypes?.length) {
      readings = readings.filter((r) => sensorTypes.includes(r.sensorType));
    }
    return readings;
  }

  subscribeToTelemetry(handler) {
    this._telemetrySubscribers.push(handler);
    return {
      unsubscribe: () => {
        this._telemetrySubscribers = this._telemetrySubscribers.filter((h) => h !== handler);
      },
    };
  }

  // ── Config / OTA — not integrated; explicit errors per STUB level ────────────

  async pushConfig()       { throw new VendorNotIntegratedError('minew', 'pushConfig'); }
  async getCurrentConfig() { throw new VendorNotIntegratedError('minew', 'getCurrentConfig'); }
  async getPendingConfig() { throw new VendorNotIntegratedError('minew', 'getPendingConfig'); }
  async rollbackConfig()   { throw new VendorNotIntegratedError('minew', 'rollbackConfig'); }
  async getDeviceHealth()  { throw new VendorNotIntegratedError('minew', 'getDeviceHealth'); }
  subscribeToEvents()      { throw new VendorNotIntegratedError('minew', 'subscribeToEvents'); }
}

/**
 * Factory for HalService.registerAdapter('minew', createMinewAdapterFactory()).
 */
export function createMinewAdapterFactory() {
  return (device) => new MinewAdapter(device);
}
